/*
 *   Copyright (c) 2025 2023 Celadon Development LLC
 *   All rights reserved.
 *   Author Aliaksandr Ziabko <aliaksandr.ziabko@celadonsoft.com>
 */
import api from "./api";

export interface Trainer {
  id: number;
  name: string;
  photoUrl?: string;
}

interface Exercise {
  name: string;
  description?: string;
}

interface MultiSet {
  exercises: Exercise[];
  type: "superset" | "triset";
  restTimeSec?: number;
}

interface WorkoutDay {
  dayNumber: number;
  title?: string;
  duration?: number;
  warmup: (Exercise | MultiSet)[];
  mainWorkout: (Exercise | MultiSet)[];
  cooldown: (Exercise | MultiSet)[];
  workoutFinisher: (Exercise | MultiSet)[];
  tips: string;
}

export interface WorkoutTrackPayload {
  type: "Strength" | "StrengthConditioning" | "Home";
  trainerId: number;
  days: WorkoutDay[];
  photoUrl?: string;
}

export interface WeekWorkoutProgramFormPayload {
  title: string;
  description: string;
  tracks: WorkoutTrackPayload[];
}

export interface WeekWorkoutProgram {
  id: number;
  title: string;
  description: string | null;
  tracks: {
    type: "Strength" | "StrengthConditioning" | "Home";
    trainer: Trainer;
    days: WorkoutDay[];
  }[];
  isActive: boolean;
}

export interface WeekWorkoutResponse {
  programs: WeekWorkoutProgram[];
  totalPages: number;
}

const API_BASE = "/week-workouts";

export const getWeekWorkoutPrograms = async (
  page: number = 1,
  limit: number = 10,
  trainerId?: number | ""
): Promise<WeekWorkoutResponse> => {
  const params: any = { page, limit };
  if (trainerId) params.trainerId = trainerId;

  const response = await api.get<WeekWorkoutResponse>(API_BASE, { params });
  return response.data;
};

export const createWeekWorkoutProgram = async (
  payload: WeekWorkoutProgramFormPayload
): Promise<WeekWorkoutProgram> => {
  const response = await api.post<WeekWorkoutProgram>(API_BASE, payload);
  return response.data;
};

export const updateWeekWorkoutProgram = async (
  id: number,
  payload: WeekWorkoutProgramFormPayload
): Promise<WeekWorkoutProgram> => {
  const response = await api.put<WeekWorkoutProgram>(
    `${API_BASE}/${id}`,
    payload
  );
  return response.data;
};

export const deleteWeekWorkoutProgram = async (id: number): Promise<void> => {
  await api.delete(`${API_BASE}/${id}`);
};

export const setWeekProgramStatus = async (id: number, isActive: boolean) => {
  const response = await api.put(`${API_BASE}/${id}/status`, { isActive });
  return response.data;
};

export const getWeekProgramCompletionsCount = async (programId: number) => {
  const res = await api.get(`${API_BASE}/${programId}/completions/count`);
  return res.data.count;
};
